/**
 * WorkspaceImpl - Implementation of the Workspace interface.
 *
 * This is the root container for the semantic domain model, managing:
 * - Domain hierarchy (root domains and their children)
 * - Bounded context registry (loaded contexts)
 *
 * Located in semantical-core with no dependencies on KnowledgeGraph,
 * Diagram, or other implementation-specific modules.
 */
/**
 * WorkspaceImpl - Concrete implementation of Workspace.
 *
 * Manages the domain hierarchy and bounded context registry.
 * UI components should interact through the Workspace interface.
 */
export class WorkspaceImpl {
    constructor(options = {}) {
        Object.defineProperty(this, "id", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "uri", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "name", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "version", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_rootDomains", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "_boundedContexts", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Map()
        });
        this.id = options.id ?? `workspace-${Date.now()}`;
        this.uri = `urn:semantical:workspace:${this.id}`;
        this.name = options.name ?? 'Workspace';
        this.version = options.version ?? 1;
        // Initialize root domains if provided
        if (options.rootDomains) {
            this._rootDomains = [...options.rootDomains];
        }
    }
    // === Domain Hierarchy ===
    getRootDomains() {
        return [...this._rootDomains];
    }
    findDomain(id) {
        const search = (domains) => {
            for (const domain of domains) {
                if (domain.id === id)
                    return domain;
                const found = search(domain.getChildren());
                if (found)
                    return found;
            }
            return undefined;
        };
        return search(this._rootDomains);
    }
    getAllDomains() {
        const result = [];
        const collect = (domains) => {
            for (const domain of domains) {
                result.push(domain);
                collect(domain.getChildren());
            }
        };
        collect(this._rootDomains);
        return result;
    }
    addRootDomain(domain) {
        if (!this._rootDomains.some(d => d.id === domain.id)) {
            this._rootDomains.push(domain);
        }
    }
    removeRootDomain(domainId) {
        const index = this._rootDomains.findIndex(d => d.id === domainId);
        if (index >= 0) {
            this._rootDomains.splice(index, 1);
            return true;
        }
        return false;
    }
    // === Bounded Context Registry ===
    getBoundedContexts() {
        return Array.from(this._boundedContexts.values());
    }
    findBoundedContext(id) {
        // First check registry
        if (this._boundedContexts.has(id)) {
            return this._boundedContexts.get(id);
        }
        // Fall back to hierarchy search
        for (const domain of this.getAllDomains()) {
            const bc = domain.getBoundedContexts().find(c => c.id === id);
            if (bc)
                return bc;
        }
        return undefined;
    }
    registerBoundedContext(context) {
        this._boundedContexts.set(context.id, context);
    }
    hasBoundedContext(id) {
        return this._boundedContexts.has(id);
    }
    // === Serialization ===
    /**
     * Convert workspace to a plain JSON object.
     * Returns a structure compatible with WorkspaceConfigRoot.
     */
    toJSON() {
        return {
            '@context': {
                '@vocab': 'http://semantical.io/ontology/core#',
                domains: { '@id': 'hasDomain', '@type': '@id', '@container': '@set' }
            },
            '@type': 'WorkspaceConfig',
            version: this.version,
            domains: this._rootDomains.map(d => 'toJSON' in d && typeof d.toJSON === 'function'
                ? d.toJSON()
                : d)
        };
    }
}
